// src/components/ui/StageLayout.tsx
// The one width-and-columns decision for a visit-pipeline stage, replacing
// the per-stage width caps that had drifted to nine independently chosen
// values between 480px and 680px across ten stages — see
// docs/HMS_V4_UI_MODERNIZATION_ASSESSMENT.md and the Pre-Consult wireframe
// (marker 2).
//
// Three variants, matching the wireframe's inventory:
//   single   — one centred readable column. For a stage that genuinely is a
//              short form (Verify, Resolve); a cap here is correct, it just
//              shouldn't be re-chosen per file.
//   three-up — a responsive card grid, 3 → 2 → 1. Pre-Consult.
//   split    — a sticky reference rail beside a fluid workspace. Consultation.
//
// `max 1560px` is the outer bound in every variant: full-width on a clinic
// monitor, but not a 3000px line length on a wall display.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type StageLayoutVariant = 'single' | 'three-up' | 'split';

export interface StageLayoutProps {
  variant?: StageLayoutVariant;
  children: ReactNode;
  className?: string;
}

/** Shared outer bound — see the note above on why it is not per-stage.
 * Widened from 1560 to 1800: on a full-width clinic monitor the split
 * variant's fixed 330px rail plus flex-1 main was leaving a visibly unused
 * margin on both sides (Consultation's own card sits at this same SHELL
 * width, so the mismatch showed up as dead space below it, not beside it). */
const SHELL = 'mx-auto w-full max-w-[1800px]';

const VARIANT_CLASS: Record<StageLayoutVariant, string> = {
  // 62ch keeps a single-column form at a readable measure without another
  // arbitrary pixel cap.
  single: 'max-w-[680px]',
  // items-stretch (the grid default — no items-start override) so every card
  // in a row shares the tallest card's height instead of each sizing to its
  // own content; the Pre-Consult A/B/C row of cards renders as one even band.
  'three-up': 'grid grid-cols-1 gap-3.5 lg:grid-cols-2 xl:grid-cols-3 items-stretch',
  // Stacks below lg, matching the wireframe's "stacks below 1000px".
  split: 'flex flex-col gap-3.5 lg:flex-row lg:items-start',
};

export function StageLayout({ variant = 'single', children, className }: StageLayoutProps) {
  return <div className={cn(SHELL, VARIANT_CLASS[variant], className)}>{children}</div>;
}

/** The sticky reference column of a `split` layout. Fixed width on desktop so
 * the workspace beside it gets every remaining pixel; full width when stacked. */
export function StageLayoutRail({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex w-full shrink-0 flex-col gap-3.5 lg:sticky lg:top-2 lg:w-[330px]', className)}>
      {children}
    </div>
  );
}

/** The working column of a `split` layout. `min-w-0` is load-bearing: without
 * it a wide child (a table, a long unbroken string) pushes the flex item past
 * its track and the whole page scrolls sideways. */
export function StageLayoutMain({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex min-w-0 flex-1 flex-col gap-3.5', className)}>{children}</div>;
}
